import React, { useState } from 'react';
import { X, CheckCircle2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const ImageCarousel = ({ photos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!photos || photos.length === 0) return null;

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? photos.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === photos.length - 1 ? 0 : currentIndex + 1);
  };

  return (
    <div className="relative w-full h-96 rounded-xl overflow-hidden bg-gray-900">
      <img
        src={photos[currentIndex].url}
        alt={photos[currentIndex].title || 'Photo du service'}
        className="w-full h-full object-contain"
      />
      
      {photos.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-2 rounded-full shadow-lg z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-2 rounded-full shadow-lg z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
            {photos.map((photo, index) => (
              <button
                key={`svc-dot-${photo.url || index}`}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-orange-600 w-6' : 'bg-white/60'
                }`}
              />
            ))}
          </div>
        </>
      )}
      
      <div className="absolute bottom-3 right-3 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
        {currentIndex + 1} / {photos.length}
      </div>
    </div>
  );
};

export const ServiceModal = ({ isOpen, onClose, service, photos = [] }) => {
  if (!service) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-4xl font-bold text-black">
            <span className="text-orange-600">{service.title}</span>
          </DialogTitle>
          <DialogDescription className="text-xl text-gray-600 mt-2">
            {service.description}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {/* Carrousel d'images */}
          {photos && photos.length > 0 && (
            <ImageCarousel photos={photos} />
          )}

          {/* Description complète */}
          <div className="prose max-w-none">
            <div className="text-gray-700 text-lg leading-relaxed whitespace-pre-line">
              {service.fullDescription}
            </div>
          </div>

          {/* Prestations/Caractéristiques */}
          {service.features && service.features.length > 0 && (
            <div className="bg-orange-50 rounded-xl p-6">
              <h3 className="text-2xl font-bold text-black mb-5">
                Nos prestations
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {service.features.map((feature, index) => (
                  <div key={`feature-${index}-${feature.slice(0, 20)}`} className="flex items-start gap-3">
                    <CheckCircle2 className="text-orange-600 flex-shrink-0 mt-1" size={22} />
                    <span className="text-gray-700 text-base">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                onClose();
                document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors text-lg"
            >
              Demander un devis gratuit
            </button>
            <button
              onClick={onClose}
              className="px-8 py-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
            >
              Fermer
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
